// web/src/admin/AdminOutbox.tsx — /admin/outbox (portal spec 22, F4): messages waiting for KC's OK. Tab 1 "To approve":
// drafts with needs_review — to, template, subject, the words as they'll be sent, the site, who wrote it; Approve
// (→ queued; the sender only sends once OUTBOX_SEND_ENABLED is on), Edit (subject + body in place, then Save or Save
// and approve), Cancel (confirm). Tab 2 "Sent and failed (7 days)", plus anything approved and still waiting to send;
// failed ones have Retry. KC never writes a new message here: the brains and the dashboard write them (spec 25's routes).
// The body can hold an invite link: shown to KC, never logged.
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { OutboxItem } from '../lib/types';
import { PageHeader } from '../components/PageHeader';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Skeleton } from '../components/Skeleton';
import { Btn, Empty, ago, fmtDate, inputCls, useAction } from './adminUi';

const STATUS_TONE: Record<OutboxItem['status'], string> = { draft: 'text-run', queued: 'text-info', sent: 'text-success', failed: 'text-danger', canceled: 'text-muted' };

export function AdminOutbox() {
  const [tab, setTab] = useState<'approve' | 'recent'>('approve');
  const drafts = useQuery({ queryKey: ['admin-outbox', 'draft'], queryFn: () => api.adminOutbox('draft') });
  const others = useQueries({ queries: (['queued', 'sent', 'failed'] as const).map((s) => ({ queryKey: ['admin-outbox', s], queryFn: () => api.adminOutbox(s), enabled: tab === 'recent' })) });
  const sites = useQuery({ queryKey: ['admin-sites'], queryFn: () => api.adminSites() });
  const slugOf = useMemo(() => new Map((sites.data ?? []).map((s) => [s.id, s])), [sites.data]);
  const toApprove = (drafts.data ?? []).filter((m) => m.needsReview);
  const week = Date.now() - 7 * 86_400_000;
  const [queued, sent, failed] = others.map((q) => q.data ?? []);
  const recent = [...(sent ?? []), ...(failed ?? [])].filter((m) => Date.parse(m.sentAt ?? m.createdAt) >= week)
    .sort((a, b) => (b.sentAt ?? b.createdAt).localeCompare(a.sentAt ?? a.createdAt));

  return (
    <>
      <PageHeader title="Outbox" subtitle="Every message to a client waits here for your OK. Nothing is sent while sending is switched off." />
      <div role="tablist" aria-label="Outbox" className="mb-4 flex gap-2">
        {([['approve', `To approve${toApprove.length ? ` (${toApprove.length})` : ''}`], ['recent', 'Sent and failed (7 days)']] as const).map(([k, label]) => (
          <button key={k} role="tab" aria-selected={tab === k} type="button" onClick={() => setTab(k)}
            className={`min-h-11 rounded-full border px-4 text-sm ${tab === k ? 'border-run bg-run/10 text-ink' : 'border-hair text-muted'}`}>{label}</button>
        ))}
      </div>
      {tab === 'approve' && (
        <>
          {drafts.isLoading && <Skeleton variant="card" count={2} />}
          {drafts.data && toApprove.length === 0 && <Empty>Nothing waiting for your OK.</Empty>}
          <div className="flex min-w-0 flex-col gap-3">
            {toApprove.map((m) => <Message key={m.id} m={m} site={m.siteId ? slugOf.get(m.siteId) : undefined} />)}
          </div>
        </>
      )}
      {tab === 'recent' && (
        <div className="flex min-w-0 flex-col gap-3">
          {others.some((q) => q.isLoading) && <Skeleton variant="card" count={2} />}
          {(queued ?? []).length > 0 && <h2 className="font-head text-sm text-muted">Approved, waiting to send</h2>}
          {(queued ?? []).map((m) => <Message key={m.id} m={m} site={m.siteId ? slugOf.get(m.siteId) : undefined} />)}
          {(queued ?? []).length > 0 && <h2 className="mt-2 font-head text-sm text-muted">Sent and failed</h2>}
          {!others.some((q) => q.isLoading) && recent.length === 0 && <Empty>Nothing sent or failed in the last 7 days.</Empty>}
          {recent.map((m) => <Message key={m.id} m={m} site={m.siteId ? slugOf.get(m.siteId) : undefined} />)}
        </div>
      )}
    </>
  );
}

function Message({ m, site }: { m: OutboxItem; site?: { slug: string; name: string } }) {
  const { busy, run } = useAction();
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(m.subject ?? '');
  const [body, setBody] = useState(m.body ?? '');
  const [canceling, setCanceling] = useState(false);
  const editable = m.status === 'draft' || m.status === 'failed';
  const changed = subject !== (m.subject ?? '') || body !== (m.body ?? '');

  const save = async (approve: boolean) => {
    if (changed) {
      const ok = await run('edit', () => api.adminEditOutbox(m.id, { subject, body }), approve ? 'Saved.' : 'Saved. It still waits for your OK.');
      if (!ok) return;
    }
    if (approve) await run('approve', () => (m.status === 'failed' ? api.adminRetryOutbox(m.id) : api.adminApproveOutbox(m.id)), 'Approved. It goes out once sending is switched on.');
    setEditing(false);
  };

  return (
    <article className="min-w-0 rounded-lg border border-hair bg-surface p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 text-sm">
          <div className="break-all text-ink">To: {m.toEmails.join(', ') || '(no one yet)'}</div>
          <div className="text-xs text-muted">{m.template} · {site ? <Link className="underline" to={`/admin/clients/${encodeURIComponent(site.slug)}`}>{site.name}</Link> : 'no site'} · by {m.createdBy} · {ago(m.createdAt)}</div>
        </div>
        <span className={`text-xs ${STATUS_TONE[m.status]}`}>{m.status}{m.sentAt ? ` ${fmtDate(m.sentAt, true)}` : ''}</span>
      </div>
      {editing ? (
        <div className="mt-3 flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-sm"><span className="text-muted">Subject</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} /></label>
          <label className="flex flex-col gap-1 text-sm"><span className="text-muted">Message</span>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} className={`${inputCls} py-2`} /></label>
        </div>
      ) : (
        <div className="mt-3 min-w-0">
          <h3 className="break-words font-medium text-ink">{m.subject || <span className="text-warn">(no subject yet)</span>}</h3>
          <p className="mt-2 whitespace-pre-wrap break-words rounded-md bg-bg px-3 py-2 text-sm text-ink">{m.body || '(no words yet: edit to write them)'}</p>
        </div>
      )}
      {m.error && <p className="mt-2 break-words text-xs text-danger">Last error: {m.error}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        {editing ? (
          <>
            <Btn kind="primary" disabled={!!busy || !subject.trim() || !body.trim()} onClick={() => void save(true)}>Save and approve</Btn>
            <Btn disabled={!!busy || !changed} onClick={() => void save(false)}>Save</Btn>
            <Btn kind="quiet" onClick={() => { setEditing(false); setSubject(m.subject ?? ''); setBody(m.body ?? ''); }}>Stop editing</Btn>
          </>
        ) : (
          <>
            {m.status === 'draft' && <Btn kind="primary" disabled={!!busy} onClick={() => void run('approve', () => api.adminApproveOutbox(m.id), 'Approved. It goes out once sending is switched on.')}>Approve</Btn>}
            {m.status === 'failed' && <Btn kind="primary" icon="refresh" disabled={!!busy} onClick={() => void run('retry', () => api.adminRetryOutbox(m.id), 'Queued again.')}>Retry</Btn>}
            {editable && <Btn disabled={!!busy} onClick={() => setEditing(true)}>Edit</Btn>}
            {(editable || m.status === 'queued') && <Btn kind="danger" disabled={!!busy} onClick={() => setCanceling(true)}>Cancel</Btn>}
          </>
        )}
      </div>
      <ConfirmDialog open={canceling} destructive title="Cancel this message?" body="It will never be sent. The brain that wrote it isn’t told." confirmLabel="Cancel message"
        onConfirm={() => { setCanceling(false); void run('cancel', () => api.adminCancelOutbox(m.id), 'Message canceled.'); }} onCancel={() => setCanceling(false)} />
    </article>
  );
}
